import supabase from '../supabase/supabase';

const normalizeType = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const roundMoney = (value) =>
  Math.round(
    (Number(value || 0) + Number.EPSILON) * 100
  ) / 100;

const isIncomeAccount = (account) => {
  const type = normalizeType(account?.type);
  const subType = normalizeType(account?.sub_type);

  return (
    type === 'income' ||
    type === 'revenue' ||
    subType === 'income' ||
    subType === 'revenue'
  );
};

const isExpenseAccount = (account) => {
  const type = normalizeType(account?.type);
  const subType = normalizeType(account?.sub_type);

  return (
    type === 'expense' ||
    type === 'expenses' ||
    subType === 'expense' ||
    subType === 'expenses'
  );
};

export const calculationService = {
  // ============================================================
  // ACCOUNTS
  // ============================================================

  async getAccounts(organizationId) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    const { data, error } = await supabase
      .schema('accounting')
      .from('accounts')
      .select(`
        id,
        organization_id,
        code,
        name,
        type,
        sub_type,
        is_active
      `)
      .eq(
        'organization_id',
        organizationId
      )
      .order('code', {
        ascending: true,
        nullsFirst: true
      });

    if (error) {
      console.error(
        'Failed to get accounts:',
        error
      );

      throw error;
    }

    return data || [];
  },

  // ============================================================
  // TRANSACTIONS
  // ============================================================

  async getTransactions(
    organizationId,
    startDate = null,
    endDate = null
  ) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    let query = supabase
      .schema('accounting')
      .from('transactions')
      .select(`
        id,
        organization_id,
        account_id,
        date,
        description,
        debit,
        credit,
        source,
        status,
        accounts:account_id (
          id,
          code,
          name,
          type,
          sub_type
        )
      `)
      .eq(
        'organization_id',
        organizationId
      );

    if (startDate) {
      query = query.gte(
        'date',
        startDate
      );
    }

    if (endDate) {
      query = query.lte(
        'date',
        endDate
      );
    }

    query = query.order('date', {
      ascending: true
    });

    const { data, error } =
      await query;

    if (error) {
      console.error(
        'Failed to get transactions:',
        error
      );

      throw error;
    }

    return data || [];
  },

  // ============================================================
  // BASIC COUNTS
  // ============================================================

  async getAccountCount(
    organizationId
  ) {
    const accounts =
      await this.getAccounts(
        organizationId
      );

    return accounts.length;
  },

  async getTransactionCount(
    organizationId,
    startDate = null,
    endDate = null
  ) {
    const transactions =
      await this.getTransactions(
        organizationId,
        startDate,
        endDate
      );

    return transactions.length;
  },

  // ============================================================
  // BASIC TOTALS
  // ============================================================

  async getTotalDebits(
    organizationId,
    startDate = null,
    endDate = null
  ) {
    const transactions =
      await this.getTransactions(
        organizationId,
        startDate,
        endDate
      );

    return roundMoney(
      transactions.reduce(
        (total, transaction) =>
          total +
          Number(
            transaction.debit || 0
          ),
        0
      )
    );
  },

  async getTotalCredits(
    organizationId,
    startDate = null,
    endDate = null
  ) {
    const transactions =
      await this.getTransactions(
        organizationId,
        startDate,
        endDate
      );

    return roundMoney(
      transactions.reduce(
        (total, transaction) =>
          total +
          Number(
            transaction.credit || 0
          ),
        0
      )
    );
  },

  async getNetTransactionAmount(
    organizationId,
    startDate = null,
    endDate = null
  ) {
    const transactions =
      await this.getTransactions(
        organizationId,
        startDate,
        endDate
      );

    const totalDebits =
      transactions.reduce(
        (total, transaction) =>
          total +
          Number(
            transaction.debit || 0
          ),
        0
      );

    const totalCredits =
      transactions.reduce(
        (total, transaction) =>
          total +
          Number(
            transaction.credit || 0
          ),
        0
      );

    return roundMoney(
      totalDebits -
        totalCredits
    );
  },

  // ============================================================
  // TRANSACTION SUMMARY
  // ============================================================

  async getTransactionSummary(
    organizationId,
    startDate = null,
    endDate = null
  ) {
    const transactions =
      await this.getTransactions(
        organizationId,
        startDate,
        endDate
      );

    const totalDebits =
      transactions.reduce(
        (total, transaction) =>
          total +
          Number(
            transaction.debit || 0
          ),
        0
      );

    const totalCredits =
      transactions.reduce(
        (total, transaction) =>
          total +
          Number(
            transaction.credit || 0
          ),
        0
      );

    return {
      transactionCount:
        transactions.length,

      totalDebits:
        roundMoney(
          totalDebits
        ),

      totalCredits:
        roundMoney(
          totalCredits
        ),

      netAmount:
        roundMoney(
          totalDebits -
            totalCredits
        )
    };
  },

  // ============================================================
  // ACCOUNT SUMMARY
  // ============================================================

  async getAccountSummary(
    organizationId
  ) {
    const accounts =
      await this.getAccounts(
        organizationId
      );

    const summary = {
      total: accounts.length,
      active: 0,
      inactive: 0,
      byType: {},
      accounts
    };

    accounts.forEach(
      (account) => {
        if (account.is_active) {
          summary.active++;
        } else {
          summary.inactive++;
        }

        const type =
          account.type ||
          'Unknown';

        if (!summary.byType[type]) {
          summary.byType[type] = 0;
        }

        summary.byType[type]++;
      }
    );

    return summary;
  },

  // ============================================================
  // PROFIT & LOSS CALCULATION
  // ============================================================

  async getProfitLoss(
    organizationId,
    startDate = null,
    endDate = null
  ) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    const transactions =
      await this.getTransactions(
        organizationId,
        startDate,
        endDate
      );

    let revenue = 0;
    let expenses = 0;

    let revenueDebits = 0;
    let revenueCredits = 0;

    let expenseDebits = 0;
    let expenseCredits = 0;

    const revenueAccounts = {};
    const expenseAccounts = {};

    transactions.forEach(
      (transaction) => {
        const account =
          transaction.accounts;

        if (!account) {
          return;
        }

        const debit =
          Number(
            transaction.debit || 0
          );

        const credit =
          Number(
            transaction.credit || 0
          );

        // ======================================================
        // INCOME
        // ======================================================

        if (
          isIncomeAccount(account)
        ) {
          const amount =
            credit - debit;

          revenue += amount;

          revenueDebits += debit;
          revenueCredits += credit;

          if (
            !revenueAccounts[
              account.id
            ]
          ) {
            revenueAccounts[
              account.id
            ] = {
              id: account.id,
              code: account.code,
              name: account.name,
              type: account.type,
              subType:
                account.sub_type,
              debits: 0,
              credits: 0,
              amount: 0
            };
          }

          revenueAccounts[
            account.id
          ].debits += debit;

          revenueAccounts[
            account.id
          ].credits += credit;

          revenueAccounts[
            account.id
          ].amount += amount;
        }

        // ======================================================
        // EXPENSES
        // ======================================================

        if (
          isExpenseAccount(account)
        ) {
          const amount =
            debit - credit;

          expenses += amount;

          expenseDebits += debit;
          expenseCredits += credit;

          if (
            !expenseAccounts[
              account.id
            ]
          ) {
            expenseAccounts[
              account.id
            ] = {
              id: account.id,
              code: account.code,
              name: account.name,
              type: account.type,
              subType:
                account.sub_type,
              debits: 0,
              credits: 0,
              amount: 0
            };
          }

          expenseAccounts[
            account.id
          ].debits += debit;

          expenseAccounts[
            account.id
          ].credits += credit;

          expenseAccounts[
            account.id
          ].amount += amount;
        }
      }
    );

    revenue =
      roundMoney(revenue);

    expenses =
      roundMoney(expenses);

    revenueDebits =
      roundMoney(revenueDebits);

    revenueCredits =
      roundMoney(revenueCredits);

    expenseDebits =
      roundMoney(expenseDebits);

    expenseCredits =
      roundMoney(expenseCredits);

    const netProfit =
      roundMoney(
        revenue -
          expenses
      );

    /*
     * At this stage COGS is still represented as an
     * Expenses account. We can split COGS from operating
     * expenses later when the reporting structure is finalized.
     */
    const grossProfit =
      netProfit;

    const profitMargin =
      revenue !== 0
        ? roundMoney(
            (netProfit /
              revenue) *
              100
          )
        : 0;

    let status =
      'break_even';

    if (netProfit > 0) {
      status =
        'profitable';
    } else if (netProfit < 0) {
      status =
        'loss';
    }

    const sortedRevenueAccounts =
      Object.values(
        revenueAccounts
      ).sort(
        (a, b) =>
          Math.abs(b.amount) -
          Math.abs(a.amount)
      );

    const sortedExpenseAccounts =
      Object.values(
        expenseAccounts
      ).sort(
        (a, b) =>
          Math.abs(b.amount) -
          Math.abs(a.amount)
      );

    return {
      revenue,
      expenses,
      grossProfit,
      netProfit,
      profitMargin,
      status,

      revenueBreakdown: {
        debits:
          revenueDebits,

        credits:
          revenueCredits,

        accounts:
          sortedRevenueAccounts
      },

      expenseBreakdown: {
        debits:
          expenseDebits,

        credits:
          expenseCredits,

        accounts:
          sortedExpenseAccounts
      }
    };
  },

  // ============================================================
  // ALL FINANCIAL CALCULATIONS
  // ============================================================

  async getFinancialCalculations(
    organizationId,
    startDate = null,
    endDate = null
  ) {
    if (!organizationId) {
      throw new Error(
        'Organization ID is required.'
      );
    }

    console.log(
      '========== FINANCIAL CALCULATIONS =========='
    );

    console.log(
      'Organization:',
      organizationId
    );

    console.log(
      'Period:',
      startDate,
      '→',
      endDate
    );

    const [
      accountSummary,
      transactionSummary,
      profitLoss
    ] = await Promise.all([
      this.getAccountSummary(
        organizationId
      ),

      this.getTransactionSummary(
        organizationId,
        startDate,
        endDate
      ),

      this.getProfitLoss(
        organizationId,
        startDate,
        endDate
      )
    ]);

    const result = {
      organizationId,

      period: {
        startDate,
        endDate
      },

      accounts:
        accountSummary,

      transactions:
        transactionSummary,

      profitLoss,

      revenue: {
        total:
          profitLoss.revenue,

        accounts:
          profitLoss
            .revenueBreakdown
            .accounts
      },

      expenses: {
        total:
          profitLoss.expenses,

        accounts:
          profitLoss
            .expenseBreakdown
            .accounts
      },

      profitCalculation: {
        formula:
          'Net Profit = Revenue - Expenses',

        revenue:
          profitLoss.revenue,

        expenses:
          profitLoss.expenses,

        grossProfit:
          profitLoss.grossProfit,

        netProfit:
          profitLoss.netProfit,

        profitMargin:
          profitLoss.profitMargin,

        status:
          profitLoss.status
      }
    };

    console.log(
      'Revenue:',
      result.profitCalculation.revenue
    );

    console.log(
      'Expenses:',
      result.profitCalculation.expenses
    );

    console.log(
      'Net Profit:',
      result.profitCalculation.netProfit
    );

    console.log(
      'Profit Margin:',
      result.profitCalculation.profitMargin
    );

    console.log(
      '============================================'
    );

    return result;
  }
};